import { AiEntityProposalType, AiFieldProposalStatus, ResolveAiFieldProposalInput } from "src/generated/graphqlTypes";
import { ObjectConfig, ObjectState } from "src/utils/formState";
import { AiFieldProposalFormValue, aiFieldProposalConfig } from "./aiFieldProposalForm";
import { type AiFormAepFragment, type AiFormAfpFragment } from "./aiProposalForm.generated";

/**
 * The bits every tab reviewing AI proposals needs, regardless of what it's reviewing.
 */

/** One server proposal as the form holds it. `status.code` flattens to `status`. */
export function toAiFieldProposalFormValue(fp: AiFormAfpFragment): AiFieldProposalFormValue {
  return {
    id: fp.id,
    fieldName: fp.fieldName,
    fieldValue: fp.fieldValue,
    status: fp.status.code,
    parentType: fp.parentType,
  };
}

/** Every field proposal under a set of entity proposals, flattened for a form's list field. */
export function flattenEntityProposals(
  entityProposals: readonly { fieldProposals: AiFormAfpFragment[] }[] | null | undefined,
): AiFieldProposalFormValue[] {
  return (entityProposals ?? []).flatMap((aep) => aep.fieldProposals.map(toAiFieldProposalFormValue));
}

/**
 * Keys entity proposals by the id of the entity they're about, for tabs reviewing many entities at
 * once (a table of rooms, options, elevations…).
 *
 * `typename` narrows the `AiEntityProposalEntity` union — pass `"ReadyPlanLocation"`,
 * `"ReadyPlanOption"`, etc. Proposals whose entity is absent or of another type are dropped: there is
 * no row to attach them to. Note that even a CREATE proposal points at an entity
 * that already exists, so nothing legitimate is lost.
 */
export function indexEntityProposals(
  entityProposals: readonly AiFormAepFragment[] | null | undefined,
  typename: string,
): Map<string, AiFormAepFragment> {
  return new Map(
    (entityProposals ?? []).flatMap((aep) => {
      const entityId = aep.entity?.__typename === typename ? aep.entity.id : undefined;
      return entityId ? [[entityId, aep] as const] : [];
    }),
  );
}

/**
 * The rows a save should send to `resolveAiFieldProposals`.
 *
 * Takes `changedValue.fieldProposals`, so rows the user never touched are left out.
 */
export function toResolveInputs(
  rows: (Partial<AiFieldProposalFormValue> | undefined)[] | undefined,
): ResolveAiFieldProposalInput[] {
  return (rows ?? []).flatMap((row) => (row?.id && row.status ? [{ id: row.id, status: row.status }] : []));
}

export function hasPendingProposals(rows: readonly ObjectState<AiFieldProposalFormValue>[]): boolean {
  return rows.some((row) => row.status.value === AiFieldProposalStatus.Pending);
}

/**
 * Whether resolving these decided statuses removes the entity: every status rejected for a CREATE (the
 * row already exists, so ignoring it undoes that) or accepted for a DELETE. Undecided proposals are
 * ignored; no decisions at all means no removal.
 */
export function entityIsRemoved(
  type: AiEntityProposalType,
  rows: readonly ObjectState<AiFieldProposalFormValue>[],
): boolean {
  const statuses = rows.map((row) => row.status.value).compact();
  if (statuses.isEmpty) return false;
  return (
    (type === AiEntityProposalType.Create && statuses.every((s) => s === AiFieldProposalStatus.Rejected)) ||
    (type === AiEntityProposalType.Delete && statuses.every((s) => s === AiFieldProposalStatus.Accepted))
  );
}

/**
 * Shared proposal fields that can be composed with the values under review in one autosaving form.
 * LocationsTab combines these fields with its writable room list.
 */
export type AiProposalsFormValue = { fieldProposals: AiFieldProposalFormValue[] };

export const aiProposalsConfig: ObjectConfig<AiProposalsFormValue> = {
  fieldProposals: { type: "list", update: "incremental", config: aiFieldProposalConfig },
};
